import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Helper function to replace hardcoded colors in style objects
export const applyThemeToStyle = (style, theme) => {
  if (!style || !theme) return style;
  
  const newStyle = { ...style };
  
  // Replace background colors
  if (newStyle.backgroundColor === '#0a0a0a') {
    newStyle.backgroundColor = theme.primaryBg;
  }
  if (newStyle.backgroundColor === '#1a1a1a') {
    newStyle.backgroundColor = theme.secondaryBg;
  }
  if (newStyle.backgroundColor === '#3b198e' || newStyle.backgroundColor === '#2d1470') {
    newStyle.backgroundColor = theme.themeColor;
  }
  
  // Replace border colors
  if (newStyle.border === '1px solid #4a4a4a' || newStyle.borderColor === '#4a4a4a') {
    newStyle.borderColor = theme.outlineColor;
    if (newStyle.border) {
      newStyle.border = `1px solid ${theme.outlineColor}`;
    }
  }
  
  // Replace text colors
  if (newStyle.color === '#3b198e') {
    newStyle.color = theme.themeColor;
  }
  
  return newStyle;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    primaryBg: '#0a0a0a',
    secondaryBg: '#1a1a1a',
    outlineColor: '#4a4a4a',
    themeColor: '#3b198e',
    themeColorHover: '#2d1470'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchThemeSettings = async () => {
      try {
        const response = await axios.get('/api/theme');
        if (response.data.success) {
          const themeData = response.data.data;
          const newTheme = {
            primaryBg: themeData.theme_primary_bg || '#0a0a0a',
            secondaryBg: themeData.theme_secondary_bg || '#1a1a1a',
            outlineColor: themeData.theme_outline_color || '#4a4a4a',
            themeColor: themeData.theme_color || '#3b198e'
          };
          // Calculate hover color (slightly darker)
          newTheme.themeColorHover = adjustColorBrightness(newTheme.themeColor, -20);
          setTheme(newTheme);
        }
      } catch (error) {
        console.error('Failed to fetch theme settings, using defaults:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchThemeSettings();
  }, []);

  // Apply theme colors to CSS variables
  useEffect(() => {
    if (!loading) {
      document.documentElement.style.setProperty('--primary-bg', theme.primaryBg);
      document.documentElement.style.setProperty('--secondary-bg', theme.secondaryBg);
      document.documentElement.style.setProperty('--outline-color', theme.outlineColor);
      document.documentElement.style.setProperty('--theme-color', theme.themeColor);
      document.documentElement.style.setProperty('--theme-color-hover', theme.themeColorHover);
    }
  }, [theme, loading]);

  return (
    <ThemeContext.Provider value={{ theme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Helper function to adjust color brightness
function adjustColorBrightness(color, amount) {
  const hex = color.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
